import { useContext, useMemo } from 'react';
import { AuthContext } from '../context/AuthContext';

// `EditInScopeOrganisationalUnitsContactInfo` is spelled with an "s" in the backend seed data, unlike
// its sibling. The name is the permission's identity, so it is matched exactly as seeded.
export const ORG_UNIT_PERMISSIONS = {
  manage: 'ManageOrganizationalUnits',
  viewInScope: 'ViewInScopeOrganizationalUnits',
  editContactInfoInScope: 'EditInScopeOrganisationalUnitsContactInfo',
} as const;

export type OrgUnitEditorMode = 'full' | 'contactInfo' | 'readonly';

export type OrgUnitAccess = {
  canManage: boolean;
  canView: boolean;
  canEditContactInfo: boolean;
  editorMode: OrgUnitEditorMode;
};

// Each permission implies the ones below it: managing implies everything, and editing a unit's
// contact info implies being able to open that unit's editor.
export function getOrgUnitAccess(permissions: Record<string, unknown> | null | undefined): OrgUnitAccess {
  const has = (name: string) => !!permissions?.[name];

  const canManage = has(ORG_UNIT_PERMISSIONS.manage);
  const canEditContactInfo = canManage || has(ORG_UNIT_PERMISSIONS.editContactInfoInScope);
  const canView = canEditContactInfo || has(ORG_UNIT_PERMISSIONS.viewInScope);

  const editorMode: OrgUnitEditorMode = canManage ? 'full' : canEditContactInfo ? 'contactInfo' : 'readonly';

  return { canManage, canView, canEditContactInfo, editorMode };
}

export function useOrgUnitAccess(): OrgUnitAccess {
  const auth = useContext(AuthContext);
  const permissions = auth?.user?.permissions;

  return useMemo(() => getOrgUnitAccess(permissions), [permissions]);
}
